import React, { useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// Tokens — match storefront / styles.js
const ink = "#0A0A0F";
const paper = "#F4F2EC";
const white = "#FFFFFF";
const accent = "#0033FF";
const muted = "rgba(10,10,15,0.58)";
const success = "#22c55e";
const danger = "#ef4444";
const fontBody = "'Archivo', system-ui, sans-serif";
const fontDisplay = "'Archivo Black', 'Archivo', sans-serif";
const fontMono = "'JetBrains Mono', monospace";

export default function ClockInPage() {
  const [empId, setEmpId] = useState("");
  const [password, setPassword] = useState("");
  const savedEmp = (() => { try { const s = sessionStorage.getItem("clock-user"); return s ? JSON.parse(s) : null; } catch { return null; } })();
  const [step, setStep] = useState(savedEmp ? "actions" : "id");
  const [employee, setEmployee] = useState(savedEmp ? { id: savedEmp.empId, name: savedEmp.name, position: savedEmp.position, clockAccounts: savedEmp.clockAccounts || [] } : null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  // Full registry of billing accounts (nycmfg + client brands like Darkfin Labs).
  const [accounts, setAccounts] = useState([{ key: "nycmfg", label: "NYC MFG" }]);
  // Which account the next Clock In will use / which account the open session is on.
  const [selectedAccount, setSelectedAccount] = useState("nycmfg");

  React.useEffect(() => {
    fetch(`${API_URL}/api/clock-accounts`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d) && d.length) setAccounts(d); })
      .catch(() => {});
    if (savedEmp) {
      fetch(`${API_URL}/api/timeclock/status/${savedEmp.empId}`)
        .then((r) => r.json())
        .then((d) => { setStatus(d.status); if (d.entry && d.entry.account) setSelectedAccount(d.entry.account); })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Accounts this employee may punch into: their home account plus any assigned brands.
  const myAccounts = accounts.filter((a) => a.key === "nycmfg" || (employee?.clockAccounts || []).includes(a.key));

  const lookupEmployee = () => {
    setLoading(true);
    fetch(`${API_URL}/api/employees/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empId, password }),
    })
      .then((r) => r.json())
      .then((data) => {
        setLoading(false);
        if (!data.success) {
          setError(data.error || "Invalid ID or password");
          return;
        }
        const emp = data.employee;
        setEmployee({ id: emp.empId, name: emp.name, position: emp.position, clockAccounts: emp.clockAccounts || [] });
        sessionStorage.setItem("clock-user", JSON.stringify(emp));
        setError("");
        return fetch(`${API_URL}/api/timeclock/status/${emp.empId}`);
      })
      .then((r) => r && r.json())
      .then((data) => {
        if (data) { setStatus(data.status); if (data.entry && data.entry.account) setSelectedAccount(data.entry.account); setStep("actions"); }
      })
      .catch(() => {
        setLoading(false);
        setError("Connection error");
      });
  };

  const punch = (action, account = selectedAccount) => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/timeclock/punch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ employeeId: employee.id, employeeName: employee.name, action, account }),
    })
      .then((r) => r.json())
      .then((data) => {
        setLoading(false);
        if (data.error) {
          setError(data.error);
        } else {
          setMessage(data.message);
          setStep("done");
        }
      })
      .catch(() => {
        setLoading(false);
        setError("Connection error. Please try again.");
      });
  };

  const reset = () => {
    sessionStorage.removeItem("clock-user");
    setEmpId("");
    setPassword("");
    setStep("id");
    setEmployee(null);
    setMessage("");
    setError("");
    setStatus(null);
  };

  const containerStyle = {
    minHeight: "100vh",
    background: paper,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: fontBody, padding: 20,
  };

  const cardStyle = {
    width: "100%", maxWidth: 440,
    background: white, border: `2px solid ${ink}`,
    borderRadius: 0, padding: "40px 32px", textAlign: "center",
    boxShadow: `4px 4px 0 ${ink}`,
  };

  const labelStyle = {
    display: "block",
    fontFamily: fontMono,
    fontSize: 11,
    fontWeight: 700,
    color: muted,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 8,
    textAlign: "left",
  };

  const inputBase = {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 0,
    border: `2px solid ${ink}`,
    background: white,
    color: ink,
    fontFamily: fontBody,
    outline: "none",
  };

  const actionButtonBase = (bg, fg, disabled) => ({
    width: "100%",
    padding: "20px 20px",
    borderRadius: 0,
    border: `2px solid ${disabled ? ink : bg}`,
    background: disabled ? "transparent" : bg,
    color: disabled ? muted : fg,
    fontSize: 16,
    fontWeight: 800,
    cursor: disabled ? "default" : "pointer",
    fontFamily: fontBody,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    opacity: loading ? 0.6 : 1,
    transition: "all .15s",
  });

  const isClockedIn = status === "clock_in";
  const isOnPO = status === "post_office";
  const canPO = isClockedIn;
  const canClockOut = isClockedIn || isOnPO;

  return (
    <div style={containerStyle}>
      <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&family=Archivo+Black&family=JetBrains+Mono:wght@400;500;700&display=swap');
          *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
          body{background:${paper};color:${ink}}
        `}</style>
      <div style={cardStyle}>
        {/* Logo */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{
            fontFamily: fontDisplay,
            fontSize: 32,
            fontWeight: 900,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
            color: ink,
            marginBottom: 8,
          }}>
            NYC Manufacturing
          </h1>
          <p style={{
            fontFamily: fontMono,
            fontSize: 11,
            color: muted,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}>
            ◆ Employee Time Clock
          </p>
        </div>

        {/* Step 1: Enter Employee ID */}
        {step === "id" && (
          <div>
            <label style={labelStyle}>Employee ID</label>
            <input
              type="text"
              value={empId}
              onChange={(e) => { setEmpId(e.target.value); setError(""); }}
              placeholder="e.g. 01"
              autoFocus
              style={{
                ...inputBase,
                fontSize: 22,
                fontFamily: fontMono,
                fontWeight: 700,
                textAlign: "center",
                letterSpacing: 6,
              }}
              onKeyDown={(e) => { if (e.key === "Enter") document.getElementById("clock-pw").focus(); }}
            />
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Password</label>
              <input
                id="clock-pw"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder="Enter your password"
                style={{
                  ...inputBase,
                  fontSize: 16,
                  textAlign: "center",
                }}
                onKeyDown={(e) => { if (e.key === "Enter") lookupEmployee(); }}
              />
            </div>
            {error && (
              <div style={{
                marginTop: 12,
                padding: "10px 14px",
                background: "transparent",
                border: `2px solid ${danger}`,
                color: danger,
                fontFamily: fontMono,
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                textAlign: "left",
              }}>
                {error}
              </div>
            )}
            <button
              onClick={lookupEmployee}
              disabled={loading}
              style={{
                width: "100%",
                padding: "16px 20px",
                borderRadius: 0,
                marginTop: 18,
                border: `2px solid ${accent}`,
                background: accent,
                color: white,
                fontSize: 14,
                fontWeight: 800,
                cursor: loading ? "default" : "pointer",
                fontFamily: fontBody,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "Verifying…" : "Continue →"}
            </button>
          </div>
        )}

        {/* Step 2: Actions */}
        {step === "actions" && employee && (
          <div>
            <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: `2px solid ${ink}` }}>
              <div style={{
                fontFamily: fontDisplay,
                fontSize: 26,
                fontWeight: 900,
                color: ink,
                marginBottom: 6,
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
                lineHeight: 1.05,
              }}>
                {employee.name}
              </div>
              <div style={{
                fontFamily: fontMono,
                fontSize: 12,
                color: muted,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 600,
              }}>
                {employee.position} · ID {employee.id}
              </div>
              {isClockedIn && (
                <div style={{
                  marginTop: 14,
                  display: "inline-block",
                  padding: "6px 12px",
                  background: success,
                  color: "#fff",
                  fontFamily: fontMono,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  border: `2px solid ${success}`,
                }}>
                  ● Currently Clocked In
                </div>
              )}
              {isOnPO && (
                <div style={{
                  marginTop: 14,
                  display: "inline-block",
                  padding: "6px 12px",
                  background: accent,
                  color: "#fff",
                  fontFamily: fontMono,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  border: `2px solid ${accent}`,
                }}>
                  ● At Post Office
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* One clock-in button per account: NYC MFG ("Clock In") plus any
                  client brands the employee is assigned to (e.g. Darkfin Labs).
                  Tapping one switches the open session onto that account. */}
              {myAccounts.map((a) => {
                const isNyc = a.key === "nycmfg";
                const active = isClockedIn && selectedAccount === a.key;
                // NYC MFG ("Clock In") only starts a fresh session. A client
                // account only switches in while already working — and that
                // switch clocks NYC MFG out. The next Post Office / Clock Out
                // then ends the client time.
                const enabled = isNyc
                  ? !(isClockedIn || isOnPO)
                  : (isClockedIn && selectedAccount !== a.key);
                const labelTxt = isNyc
                  ? (active ? "✓ Clocked In" : isClockedIn ? "Clocked Out" : isOnPO ? "✓ Clocked In" : "Clock In")
                  : (active ? `✓ On ${a.label}` : a.label);
                return (
                  <button
                    key={a.key}
                    onClick={() => punch("clock_in", a.key)}
                    disabled={loading || !enabled}
                    style={actionButtonBase(isNyc ? success : ink, white, !enabled)}
                  >
                    {labelTxt}
                  </button>
                );
              })}
              <button
                onClick={() => punch("post_office")}
                disabled={loading || !canPO}
                style={actionButtonBase(accent, white, !canPO)}
              >
                {isOnPO ? "✓ At Post Office" : "Post Office Run"}
              </button>
              <button
                onClick={() => punch("clock_out")}
                disabled={loading || !canClockOut}
                style={actionButtonBase(danger, white, !canClockOut)}
              >
                Clock Out
              </button>
            </div>

            {error && (
              <div style={{
                marginTop: 12,
                padding: "10px 14px",
                background: "transparent",
                border: `2px solid ${danger}`,
                color: danger,
                fontFamily: fontMono,
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                textAlign: "left",
              }}>
                {error}
              </div>
            )}

            <button onClick={reset} style={{
              marginTop: 18,
              background: "transparent",
              border: `2px solid ${ink}`,
              borderRadius: 0,
              padding: "10px 20px",
              color: ink,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: fontBody,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}>
              Switch Employee
            </button>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === "done" && (
          <div>
            <div style={{ fontSize: 56, marginBottom: 16 }}>
              {message.includes("clocked out") ? "👋" : message.includes("switched") ? "🔄" : message.includes("Post Office") ? "📬" : "✅"}
            </div>
            <div style={{
              fontFamily: fontDisplay,
              fontSize: 22,
              fontWeight: 900,
              color: ink,
              marginBottom: 10,
              letterSpacing: "-0.01em",
              textTransform: "uppercase",
              lineHeight: 1.1,
            }}>
              {message}
            </div>
            <button onClick={reset} style={{
              marginTop: 20,
              width: "100%",
              padding: "16px 20px",
              borderRadius: 0,
              border: `2px solid ${accent}`,
              background: accent,
              color: white,
              fontSize: 14,
              fontWeight: 800,
              cursor: "pointer",
              fontFamily: fontBody,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}>
              Done
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
